import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Badge,
  Modal,
  Text,
  Paper,
  Group,
  Loader,
  Center,
  ScrollArea,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Box,
  Grid,
  Avatar,
  Divider,
  Blockquote,
  Select, // <-- 1. Import Select
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconEye,
  IconInfoCircle,
  IconRefresh,
  IconSchool,
  IconClipboardCheck, // Ikon Monitoring
  IconUser,
  IconQuote,
  IconCalendar,
  IconFilter, // <-- 2. Import Icon Filter
} from "@tabler/icons-react";
import api from "../../services/api";

export default function AdminLaporanMonitoringPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- 3. State Filter ---
  const [filterSekolah, setFilterSekolah] = useState(null);

  // Modal State
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/laporan-monitoring");
      const hasil = response.data.data || response.data;
      setData(Array.isArray(hasil) ? hasil : []);
    } catch (error) {
      console.error("Gagal Load Data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 4. LOGIKA FILTERING ---

  // A. Ambil daftar sekolah unik untuk opsi Dropdown
  const schoolOptions = Array.from(
    new Set(
      data
        .map((item) => item.sekolah?.nama_sekolah) // Ambil nama sekolah
        .filter((nama) => nama) // Hapus yang null
    )
  ).map((nama) => ({ value: nama, label: nama }));

  // B. Filter Data List berdasarkan pilihan dropdown
  const filteredData = data.filter((item) => {
    if (!filterSekolah) return true; // Tampilkan semua jika filter kosong
    return item.sekolah?.nama_sekolah === filterSekolah;
  });

  // --- 2. HANDLER VIEW ---
  const handleViewDetail = (item) => {
    setSelectedItem(item);
    open();
  };

  // --- 3. RENDER UI ---
  if (loading)
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );

  // --- GENERATE ROWS (Gunakan filteredData) ---
  const rows = filteredData.map((item) => {
    const hasReport = !!item.laporan_monitoring;
    const report = item.laporan_monitoring || {};

    return (
      <Table.Tr key={item.id}>
        {/* Kolom Tanggal Distribusi */}
        <Table.Td>
          <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString("id-ID", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Text>
        </Table.Td>

        {/* Kolom Sekolah */}
        <Table.Td>
          <Group gap="sm">
            <ThemeIcon variant="light" color="blue" radius="xl" size="lg">
              <IconSchool size={18} />
            </ThemeIcon>
            <div>
              <Text fz="sm" fw={500}>
                {item.sekolah?.nama_sekolah}
              </Text>
              <Text fz="xs" c="dimmed">
                Lokasi Distribusi
              </Text>
            </div>
          </Group>
        </Table.Td>

        {/* Kolom Status & Supervisor */}
        <Table.Td>
          {hasReport ? (
            <div>
              <Badge color="green" variant="light" mb={4}>
                Sudah Laporan
              </Badge>
              <Group gap={6}>
                <IconUser size={14} color="gray" />
                <Text fz="xs" c="dimmed">
                  {report.user?.name || "Supervisor"}
                </Text>
              </Group>
            </div>
          ) : (
            <Badge color="gray" variant="outline">
              Belum Ada
            </Badge>
          )}
        </Table.Td>

        {/* Kolom Preview Hasil */}
        <Table.Td>
          {hasReport ? (
            <Text
              size="sm"
              lineClamp={1}
              c="dimmed"
              style={{ maxWidth: 250 }}
            >
              {report.hasil_monitoring}
            </Text>
          ) : (
            <Text c="dimmed" fz="xs">
              -
            </Text>
          )}
        </Table.Td>

        {/* Kolom Aksi */}
        <Table.Td align="right">
          {hasReport && (
            <Tooltip label="Lihat Detail Monitoring">
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={() => handleViewDetail(item)}
              >
                <IconEye size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Monitoring Lapangan</Title>
          <Text c="dimmed" size="sm">
            Pantau laporan hasil monitoring distribusi dari supervisor
          </Text>
        </div>
        <Button
          variant="light"
          onClick={fetchData}
          leftSection={<IconRefresh size={16} />}
        >
          Refresh
        </Button>
      </Group>

      {/* --- 5. UI FILTER --- */}
      <Paper p="md" mb="md" withBorder radius="md" bg="gray.0">
        <Group>
          <Select
            placeholder="Filter Sekolah"
            leftSection={<IconFilter size={16} />}
            data={schoolOptions}
            value={filterSekolah}
            onChange={setFilterSekolah}
            clearable
            searchable
            nothingFoundMessage="Sekolah tidak ditemukan"
            style={{ width: 300 }}
          />
          {filterSekolah && (
            <Text size="sm" c="dimmed">
              Hasil filter: <b>{filteredData.length}</b> data ditemukan
            </Text>
          )}
        </Group>
      </Paper>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        {data.length === 0 ? (
          <Center p="xl" c="dimmed">
            <Group>
              <IconInfoCircle size={20} />
              <Text>Belum ada data distribusi.</Text>
            </Group>
          </Center>
        ) : (
          <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="gray.0">
                <Table.Tr>
                  <Table.Th>Tgl Distribusi</Table.Th>
                  <Table.Th>Sekolah Tujuan</Table.Th>
                  <Table.Th>Status & Petugas</Table.Th>
                  <Table.Th>Ringkasan Laporan</Table.Th>
                  <Table.Th ta="right">Detail</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length > 0 ? (
                  rows
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Center p="xl" c="dimmed">
                        Tidak ada data untuk sekolah <b>{filterSekolah}</b>.
                      </Center>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      {/* --- MODAL DETAIL (READ ONLY) --- */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group>
            <IconClipboardCheck size={20} color="#228be6" />
            <Text fw={700}>Detail Monitoring</Text>
          </Group>
        }
        centered
        size="lg"
      >
        {selectedItem && selectedItem.laporan_monitoring && (
          <Box>
            {/* 1. Header Info Card */}
            <Paper withBorder p="sm" mb="md" bg="gray.0" radius="md">
              <Grid>
                <Grid.Col span={6}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                    Sekolah
                  </Text>
                  <Text fw={500}>{selectedItem.sekolah?.nama_sekolah}</Text>
                </Grid.Col>
                <Grid.Col span={6} style={{ textAlign: "right" }}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                    Menu Makanan
                  </Text>
                  <Text fw={500}>{selectedItem.menu?.nama_menu}</Text>
                </Grid.Col>

                <Grid.Col span={12} py={0}>
                  <Divider color="gray.3" borderStyle="dashed" />
                </Grid.Col>

                <Grid.Col span={12} mt="xs">
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                        Petugas Monitoring
                      </Text>
                      <Group gap="xs">
                        <Avatar size="sm" color="blue" radius="xl">
                          <IconUser size={14} />
                        </Avatar>
                        <Text size="sm" fw={600}>
                          {selectedItem.laporan_monitoring.user?.name ||
                            "Supervisor"}
                        </Text>
                      </Group>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                        Tanggal Monitoring
                      </Text>
                      <Group gap={5} justify="flex-end">
                        <IconCalendar size={14} color="blue" />
                        <Text size="sm" fw={600} c="blue">
                          {new Date(
                            selectedItem.laporan_monitoring.tanggal_monitoring
                          ).toLocaleDateString("id-ID", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </Text>
                      </Group>
                    </div>
                  </Group>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* 2. Isi Laporan Monitoring */}
            <Text fw={500} mb="xs" size="sm">
              Hasil Temuan / Laporan:
            </Text>

            <Blockquote
              color="blue"
              icon={<IconQuote size={20} />}
              mt="xs"
              p="md"
              bg="blue.0"
            >
              <Text
                size="sm"
                style={{ lineHeight: 1.6, whiteSpace: "pre-wrap" }}
              >
                {selectedItem.laporan_monitoring.hasil_monitoring}
              </Text>
            </Blockquote>

            <Text c="dimmed" size="xs" mt="sm" fs="italic">
              *Laporan ini dibuat berdasarkan observasi langsung di lapangan
              pada tanggal monitoring.
            </Text>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={close}>
                Tutup
              </Button>
            </Group>
          </Box>
        )}
      </Modal>
    </div>
  );
}